import { ClientSearch } from "@core/components/client-search";
import { CopyButton } from "@core/components/copy-btn";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { TransactionTrigger } from "@core/components/transaction-trigger";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { imageAssetUrl } from "@/utils/url";
import {
  TransactionOrderType,
  TRANSACTION_CLIENT_STATES,
} from "creso-core/constants";
import { useNFOs } from "creso-core/hooks";
import { Client, NFO } from "creso-core/types";
import {
  addDays,
  format,
  formatDistanceToNowStrict,
  isWithinInterval,
} from "date-fns";
import { useMemo, useState } from "react";
import { Button, cn } from "web-design-system";
import { Page } from "@core/components/layout/page";

export const Route = createFileRoute("/(auth)/_headed/funds/_layout/nfo")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data: nfos } = useNFOs();

  return (
    <>
      <Page.Header
        title="New fund offerings"
        className="top-12"
        actions={<TransactionTrigger variant="default" />}
      />
      <Page.Body>
        <div className="flex flex-col divide-y divide-border flex-1">
          {nfos?.results.map((fund) => (
            <NFOCard key={fund.isin} fund={fund} />
          ))}
          {nfos?.results.length === 0 && (
            <div className="flex flex-1 items-center justify-center flex-col border border-border border-dashed gap-4">
              <img
                src={imageAssetUrl("portfolio/empty.png")}
                className="size-20 object-cover"
                alt="No new funds offerings available"
              />
              <p>No new fund offerings available.</p>
            </div>
          )}
        </div>
      </Page.Body>
    </>
  );
}

function NFOCard({ fund }: { fund: NFO }) {
  const ipoLessThanaWeekAway = useMemo(
    () =>
      isWithinInterval(new Date(fund.ipo_date), {
        start: new Date(),
        end: addDays(new Date(), 7),
      }),
    [fund.ipo_date]
  );

  const [client, setClient] = useState<Client>();

  const navigate = useNavigate();
  const subText = [
    fund.broad_category_group,
    fund.fund_level_category_name,
  ].join(" - ");

  const handleNavigation = (type: TransactionOrderType) => {
    navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        type,
        modal: "transaction",
        isin: fund.isin,
        client_id: client?.id,
      }),
    });
  };

  return (
    <div className="p-5 hover:bg-card group">
      <div className="flex flex-row gap-5">
        <div className="flex-1">
          <div className="flex flex-col gap-5">
            <div className="flex flex-row items-center gap-2">
              <AmcLogo amc_code={fund.morningstar_amc_code} />
              <div className="flex flex-col flex-1 gap-1">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-sm">{fund.fund_name}</span>
                </div>
                {fund.broad_category_group || fund.fund_level_category_name ? (
                  <span className="text-xs text-muted-foreground">
                    {subText}
                  </span>
                ) : null}
              </div>
              <div
                className={cn(
                  "ml-auto opacity-0 self-start group-hover:opacity-100 transition-opacity duration-300",
                  client && "opacity-100"
                )}
              >
                <CopyButton text={fund.fund_name} />
              </div>
            </div>
            <div className="flex flex-row">
              <div className="flex-1 flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Open for</span>
                <span className="font-semibold">
                  {`${format(fund.subscription_start_date, "dd MMM")}${format(fund.ipo_date, " - dd MMM yyyy")}`}
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Closing in</span>
                <span
                  className={cn(
                    "font-semibold",
                    ipoLessThanaWeekAway && "text-destructive"
                  )}
                >
                  {formatDistanceToNowStrict(new Date(fund.ipo_date))}
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Min SIP</span>
                <span className="font-semibold">₹{fund.min_sip_amount}</span>
              </div>
              <div className="flex-1 flex flex-col gap-1 text-sm">
                <span className="text-muted-foreground">Min one-time</span>
                <span className="font-semibold">
                  ₹{fund.min_purchase_amount}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          className={cn(
            "flex flex-col shrink-0 pl-5 border-l border-border justify-start opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 gap-4 transition-opacity duration-300",
            client && "opacity-100"
          )}
        >
          <div className="w-70">
            <ClientSearch
              options={{ portal: false, modal: false, align: "end" }}
              onSelect={setClient}
              status={TRANSACTION_CLIENT_STATES}
            />
          </div>
          <div className="flex flex-row items-center gap-3">
            <Button
              onClick={() => handleNavigation(TransactionOrderType.PURCHASE)}
              className="flex-1"
              variant="outline"
            >
              One Time
            </Button>
            <Button
              onClick={() => handleNavigation(TransactionOrderType.XSIP)}
              className="flex-1"
            >
              SIP
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
